import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Download, 
  Upload, 
  Warehouse, 
  FileText, 
  History, 
  LogOut,
  Menu,
  X,
  Bell,
  Users,
  ClipboardList,
  Settings,
  HardDrive,
  FolderOpen,
  FolderKanban,
  Banknote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Banknote, label: 'Project Commercials', path: '/project-commercials' },
  { icon: Package, label: 'Item Master', path: '/items' },
  { icon: Users, label: 'Supplier Master', path: '/suppliers' },
  { icon: FolderOpen, label: 'Projects', path: '/projects' },
  { icon: Download, label: 'Stock Receipt', path: '/stock-receipt' },
  { icon: Upload, label: 'Stock Issue', path: '/stock-issue' },
  { icon: Warehouse, label: 'Current Stock', path: '/current-stock' },
  { icon: ClipboardList, label: 'Material Reconciliation', path: '/material-reconciliation' },
  { icon: Settings, label: 'Turned Parts', path: '/turned-parts' },
  { icon: FileText, label: 'Reports', path: '/reports' },
  { icon: FolderKanban, label: 'Project Entries', path: '/project-entries' },
  { icon: History, label: 'History', path: '/history' },
  { icon: HardDrive, label: 'Backup', path: '/backup' },
];

function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchLowStockAlerts();
  }, []);

  const fetchLowStockAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/low-stock-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowStockCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch low stock alerts', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_store-manager-92/artifacts/9jy5zrmo_Green%20log0-%20small.png" 
                alt="WESTAW Logo" 
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold font-heading" style={{ color: '#008080' }}>WESTAW</h1>
            </div>
            <p className="text-sm text-slate-600 ml-13">Factory Inventory System</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`
                    flex items-center px-4 py-3 rounded-lg font-medium transition-all
                    ${isActive 
                      ? 'bg-gradient-to-r from-sky-50 to-transparent border-l-4 border-sky-500 text-slate-900' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                  {item.label === 'Dashboard' && lowStockCount > 0 && (
                    <Badge className="ml-auto bg-red-100 text-red-700 border-red-200" data-testid="low-stock-badge">
                      {lowStockCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-900" data-testid="user-name">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-600">{user?.email || ''}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="backdrop-blur-md bg-white/80 border-b border-slate-200/60 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              data-testid="sidebar-toggle"
            >
              {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            
            <div className="flex items-center gap-4 ml-auto">
              {lowStockCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg" data-testid="low-stock-alert">
                  <Bell className="w-4 h-4 text-red-700" />
                  <span className="text-sm font-medium text-red-700">{lowStockCount} Low Stock Alert{lowStockCount > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden" 
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default DashboardLayout;